import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import BottomQuickActions from "../components/BottomQuickActions";
import { useLiveFilter, getJobSearchText } from "../hooks/useLiveFilter";
import { useSearchFilter } from "../layouts/AppLayout";
import {
  Plus,
  Briefcase,
  User,
  CalendarBlank,
  Warning,
  UserPlus,
} from "phosphor-react";
import AdBanner from "../components/AdBanner";
import { useServiceTypes } from "../hooks/useServiceTypes";
import CustomerCreateModal from "../components/CustomerCreateModal";
import SearchableCustomerSelect from "../components/SearchableCustomerSelect";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Custom status order for sorting
const STATUS_ORDER = {
  draft: 0,
  scheduled: 1,
  in_progress: 2,
  completed: 3,
  canceled: 4,
};

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    title: "",
    description: "",
    service_type: "",
    status: "draft",
    priority: "normal",
  });
  
  // URL params for auto-opening create modal
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use shared filter from layout header
  const { jobsFilter: filterQuery } = useSearchFilter();
  
  // Service types hook
  const { 
    serviceTypes, 
    loading: serviceTypesLoading, 
    getDefaultServiceType,
    updateUserPreference,
    getIdBySlug,
    getNameBySlug,
  } = useServiceTypes();
  
  // Track if user has touched the service type field
  const userTouchedServiceType = useRef(false);
  const defaultsApplied = useRef(false);
  
  // Handle ?new=1 query param to auto-open create modal
  useEffect(() => {
    const shouldOpenNew = searchParams.get("new") === "1";
    if (shouldOpenNew && !isDialogOpen) {
      setIsDialogOpen(true);
      // Remove the param to prevent re-opening on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, isDialogOpen, navigate, location.pathname]);

  useEffect(() => {
    fetchJobs();
    fetchCustomers();
  }, [statusFilter]);

  // Memoized search text extractor for jobs
  const jobSearchText = useCallback(getJobSearchText, []);

  // Apply status filter first
  const statusFilteredJobs = useMemo(() => {
    if (statusFilter === "all") return jobs;
    return jobs.filter(job => job.status === statusFilter);
  }, [jobs, statusFilter]);

  // Live filter by text query
  const filteredJobs = useLiveFilter(statusFilteredJobs, filterQuery, jobSearchText);

  // Sort filtered jobs: status order + oldest to newest by date
  const displayedJobs = useMemo(() => {
    return [...filteredJobs].sort((a, b) => {
      // Primary sort: status order
      const statusDiff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      
      // Secondary sort: oldest to newest (use scheduled_date if present, else created_at)
      const dateA = new Date(a.scheduled_date || a.created_at || 0);
      const dateB = new Date(b.scheduled_date || b.created_at || 0);
      return dateA - dateB;
    });
  }, [filteredJobs]);

  const fetchJobs = async () => {
    try {
      const url =
        statusFilter === "all"
          ? `${API}/jobs`
          : `${API}/jobs?status=${statusFilter}`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      // Fetch initial customers for the picker (server-side search will be handled by the component)
      const response = await fetch(`${API}/customers?limit=50`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  // Apply default service type when dialog opens and service types are loaded
  useEffect(() => {
    if (isDialogOpen && serviceTypes.length > 0 && !userTouchedServiceType.current && !defaultsApplied.current) {
      const defaultType = getDefaultServiceType();
      if (defaultType && !formData.service_type) {
        setFormData(prev => ({ ...prev, service_type: defaultType }));
      }
      defaultsApplied.current = true;
    }
  }, [isDialogOpen, serviceTypes, getDefaultServiceType, formData.service_type]);

  // Reset flags when dialog closes
  useEffect(() => {
    if (!isDialogOpen) {
      userTouchedServiceType.current = false;
      defaultsApplied.current = false;
    }
  }, [isDialogOpen]);

  // Handle service type change
  const handleServiceTypeChange = (value) => {
    userTouchedServiceType.current = true;
    setFormData({ ...formData, service_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Job created successfully");
        
        // Update user preference with the selected service type
        const serviceTypeId = getIdBySlug(formData.service_type);
        if (serviceTypeId) {
          updateUserPreference(serviceTypeId);
        }
        
        setIsDialogOpen(false);
        setFormData({
          customer_id: "",
          title: "",
          description: "",
          service_type: "",
          status: "draft",
          priority: "normal",
        });
        fetchJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create job");
      }
    } catch (error) {
      console.error("Error creating job:", error);
      toast.error("Failed to create job");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
      scheduled: "bg-blue-50 text-blue-700 border-blue-300",
      in_progress: "bg-amber-50 text-amber-700 border-amber-300",
      completed: "bg-emerald-50 text-emerald-700 border-emerald-300",
      canceled: "bg-red-50 text-red-700 border-red-300",
    };
    return colors[status] || colors.draft;
  };

  const getPriorityIcon = (priority) => {
    if (priority === "urgent" || priority === "high") {
      return <Warning size={16} className="text-red-500" />;
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      {/* Controls Row - Status dropdown only (filter is in layout header) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-10 border-2" data-testid="status-filter-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Jobs</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
              data-testid="add-job-btn"
            >
              <Plus size={18} className="mr-2" />
              New Job
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="new-job-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">
                Create New Job
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form id="new-job-form" onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Customer *</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs text-[#FF6600] hover:text-[#CC5200] hover:bg-orange-50 -mr-2"
                      onClick={() => setShowQuickCustomer(true)}
                      data-testid="job-new-customer-btn"
                    >
                      <UserPlus size={14} className="mr-1" />
                      New Customer
                    </Button>
                  </div>
                  <SearchableCustomerSelect
                    customers={customers}
                    value={formData.customer_id}
                    onChange={(v) => setFormData({ ...formData, customer_id: v })}
                    label=""
                    data-testid="job-customer-select"
                  />
                </div>
                <div>
                  <Label>Job Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="Water heater installation"
                    className="mt-1 h-12 border-2"
                    required
                    data-testid="job-title-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Job details..."
                    className="mt-1 border-2"
                    rows={3}
                    data-testid="job-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Service Type</Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={handleServiceTypeChange}
                      disabled={serviceTypesLoading}
                    >
                      <SelectTrigger className="mt-1 h-12 border-2" data-testid="service-type-select">
                        <SelectValue placeholder={serviceTypesLoading ? "Loading..." : "Select service type"} />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map((type) => (
                          <SelectItem key={type.service_type_id} value={type.slug}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(v) =>
                        setFormData({ ...formData, priority: v })
                      }
                    >
                      <SelectTrigger className="mt-1 h-12 border-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="flex-1 sm:flex-none border-2"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="new-job-form"
                className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
                disabled={!formData.customer_id}
                data-testid="save-job-btn"
              >
                Create Job
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Jobs List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-zinc-200 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : displayedJobs.length > 0 ? (
        <div className="space-y-4">
          {/* Show filter indicator when filtering */}
          {filterQuery.trim() && (
            <div className="text-sm text-zinc-500 bg-zinc-100 px-3 py-2 rounded-lg" data-testid="filter-results-count">
              Showing {displayedJobs.length} of {statusFilteredJobs.length} jobs
            </div>
          )}
          {displayedJobs.map((job, index) => (
            <div key={job.job_id}>
              <Link to={`/jobs/${job.job_id}`}>
                <Card className="border-2 border-zinc-200 card-hover">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#FF6600]/10 border-2 border-[#FF6600]/30 flex items-center justify-center flex-shrink-0">
                          <Briefcase size={24} className="text-[#FF6600]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{job.title}</p>
                            {getPriorityIcon(job.priority)}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-zinc-500">
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {job.customer?.name || "No customer"}
                            </span>
                            {job.scheduled_date && (
                              <span className="flex items-center gap-1">
                                <CalendarBlank size={14} />
                                {new Date(job.scheduled_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {job.description && (
                            <p className="text-sm text-zinc-400 mt-2 line-clamp-1">
                              {job.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge
                          variant="outline"
                          className={getStatusColor(job.status)}
                        >
                          {job.status.replace("_", " ")}
                        </Badge>
                        {job.invoiced && (
                          <span className="text-xs text-emerald-600">Invoiced</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              {/* Insert ad after 10th item */}
              {index === 9 && <AdBanner placement="listview_inline" className="my-4" />}
            </div>
          ))}
        </div>
      ) : statusFilteredJobs.length > 0 && filterQuery.trim() ? (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-12 text-center">
            <Briefcase size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg font-semibold uppercase mb-2">
              No Matching Jobs
            </h3>
            <p className="text-zinc-500 mb-6">
              No jobs match your filter
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-12 text-center">
            <Briefcase size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg font-semibold uppercase mb-2">
              No Jobs Yet
            </h3>
            <p className="text-zinc-500 mb-6">
              Create your first job to get started
            </p>
            <Button
              onClick={() => setIsDialogOpen(true)}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
            >
              <Plus size={18} className="mr-2" />
              New Job
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Customer Create Modal */}
      <CustomerCreateModal
        isOpen={showQuickCustomer}
        onClose={() => setShowQuickCustomer(false)}
        onCustomerCreated={(customer) => {
          setCustomers((prev) => [customer, ...prev]);
          setFormData((prev) => ({ ...prev, customer_id: customer.customer_id }));
        }}
      />

      {/* Quick Actions */}
      <BottomQuickActions />
    </div>
  );
}
